import { useId, useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import type { PayrollRosterField } from "./types";
import {
  parseBulkImport,
  type BulkImportInvalidRow,
  type BulkImportRow,
} from "./utils";

interface ImportSummary {
  fieldLabel: string;
  matched: number;
  unmatched: string[];
  invalid: BulkImportInvalidRow[];
}

interface ImportValuesDialogProps {
  open: boolean;
  fields: PayrollRosterField[];
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (
    field: PayrollRosterField,
    rows: BulkImportRow[],
  ) => Promise<{
    matched: string[];
    unmatched: string[];
    invalid: BulkImportInvalidRow[];
  }>;
}

// Bulk-imports values for a single column, keyed by ServiceTitan employee id.
// Parsing/matching live in utils.ts; this dialog just drives them and reports
// what matched. The import is persisted to the working draft immediately;
// Publish later promotes the accumulated changes to a new roster version.
export const ImportValuesDialog = ({
  open,
  fields,
  saving,
  onOpenChange,
  onApply,
}: ImportValuesDialogProps) => {
  const [fieldKey, setFieldKey] = useState(fields[0]?.key ?? "");
  const [columnSearch, setColumnSearch] = useState(fields[0]?.label ?? "");
  const [text, setText] = useState("");
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const rowsLabelId = useId();

  const columnItems = fields
    .filter((field) =>
      field.label.toLowerCase().includes(columnSearch.toLowerCase()),
    )
    .map((field) => ({ value: field.key, label: field.label }));

  const reset = () => {
    setText("");
    setParseErrors([]);
    setSummary(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (saving) return;
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const targetField = fields.find((field) => field.key === fieldKey);

  const apply = async () => {
    if (!targetField) return;

    const { rows, errors } = parseBulkImport(text);
    setParseErrors(errors);

    if (rows.length === 0) {
      setSummary(null);
      if (errors.length === 0) {
        toast.error("Paste at least one row to import.");
      }
      return;
    }

    let result: Awaited<ReturnType<typeof onApply>>;
    try {
      result = await onApply(targetField, rows);
    } catch {
      return;
    }
    const { matched, unmatched, invalid } = result;
    setSummary({
      fieldLabel: targetField.label,
      matched: matched.length,
      unmatched,
      invalid,
    });

    const issueDescription = [
      unmatched.length > 0
        ? `${unmatched.length} ${
            unmatched.length === 1 ? "id" : "ids"
          } did not match anyone on the roster.`
        : null,
      invalid.length > 0
        ? `${invalid.length} ${
            invalid.length === 1 ? "value was" : "values were"
          } invalid and skipped.`
        : null,
    ]
      .filter(Boolean)
      .join(" ");

    if (matched.length === 0) {
      toast.error("No values were imported", {
        description: issueDescription,
      });
    } else {
      toast.success(
        `Imported ${matched.length} ${
          matched.length === 1 ? "value" : "values"
        } into ${targetField.label}`,
        issueDescription ? { description: issueDescription } : undefined,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import values</DialogTitle>
          <Text variant="caption" tone="muted">
            Paste rows keyed by ServiceTitan employee id (e.g.{" "}
            <span className="font-medium">19470, 46.0</span>). One per line,
            comma or tab separated. Matched people get the value in the chosen
            column. They are saved to the working draft so you can keep making
            changes before publishing.
          </Text>
        </DialogHeader>

        <Stack align="start" gap={4}>
          <Stack align="start" gap={1}>
            <Text variant="label">Column to fill</Text>
            <AutoComplete
              ariaLabel="Column to fill"
              fullWidth
              disabled={saving}
              selectedValue={fieldKey}
              onSelectedValueChange={setFieldKey}
              searchValue={columnSearch}
              onSearchValueChange={setColumnSearch}
              items={columnItems}
              placeholder="Select a column"
              emptyMessage="No columns"
            />
          </Stack>

          <Stack className="w-full" gap={2}>
            <Text id={rowsLabelId} variant="label">
              Rows
            </Text>
            <Textarea
              aria-labelledby={rowsLabelId}
              disabled={saving}
              placeholder={"19470, 46.0\n46821, 52.5"}
              value={text}
              className="min-h-32"
              onChange={(event) => setText(event.target.value)}
            />
          </Stack>

          {parseErrors.length > 0 && (
            <Alert color="danger">
              <Stack align="start" gap={1}>
                {parseErrors.map((error) => (
                  <span key={error}>{error}</span>
                ))}
              </Stack>
            </Alert>
          )}

          {summary && (
            <Alert
              color={
                summary.unmatched.length > 0 || summary.invalid.length > 0
                  ? "danger"
                  : "information"
              }
            >
              <Stack align="start" gap={1}>
                <span>
                  Applied {summary.matched}{" "}
                  {summary.matched === 1 ? "value" : "values"} to{" "}
                  {summary.fieldLabel}.
                </span>
                {summary.unmatched.length > 0 && (
                  <span>Unmatched ids: {summary.unmatched.join(", ")}</span>
                )}
                {summary.invalid.length > 0 && (
                  <Stack align="start" gap={0}>
                    {summary.invalid.map((row) => (
                      <span key={row.stId}>
                        {row.stId}: {row.reason}
                      </span>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Alert>
          )}
        </Stack>

        <DialogFooter>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => handleOpenChange(false)}
          >
            Done
          </Button>
          <Button
            aria-busy={saving || undefined}
            disabled={saving || !targetField || !text.trim()}
            onClick={apply}
          >
            {saving && <Spinner data-icon="inline-start" aria-hidden />}
            {saving ? "Saving roster..." : "Apply to roster"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
